/**
 * Chat Service
 *
 * Service for handling AI chatbot API calls
 *
 * @package WooAIChatbot
 * @author Roman Siromskyi <leowebmark@gmail.com>
 */
/**
 * Cached nonce that can be refreshed
 */
let cachedNonce = null;
/**
 * Whether nonce has been proactively refreshed this session
 */
let nonceRefreshed = false;
/**
 * Get API configuration from WordPress globals
 */
const getApiConfig = () => {
    const globals = window.wooAiChatbot ?? window.wooAIChatbot;
    // Use localized API URL from PHP, fallback to /wp-json for non-standard setups.
    const apiUrl = globals?.apiUrl || '';
    const restBase = apiUrl ? apiUrl.replace(/\/woo-ai\/v1\/?$/, '') : '/wp-json';
    return {
        restUrl: restBase,
        nonce: cachedNonce || globals?.nonce || '',
    };
};
/**
 * Fetch fresh nonce from server (bypasses page cache)
 * Uses cache-busting query param to avoid Varnish/CDN caching
 */
const refreshNonce = async () => {
    try {
        const { restUrl } = getApiConfig();
        const response = await fetch(`${restUrl}/woo-ai/v1/chat/nonce?_=${Date.now()}`, {
            method: 'GET',
            credentials: 'omit',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (response.ok) {
            const data = await response.json();
            cachedNonce = data.nonce;
            nonceRefreshed = true;
            // Also update globals for consistency
            const globals = window.wooAiChatbot ?? window.wooAIChatbot;
            if (globals) {
                globals.nonce = data.nonce;
            }
            return data.nonce;
        }
    }
    catch (e) {
        console.error('Failed to refresh nonce:', e);
    }
    return '';
};
/**
 * Ensure nonce is fresh before making API calls.
 * Called once on first API request to handle stale page-cached nonces.
 */
const ensureFreshNonce = async () => {
    if (!nonceRefreshed) {
        await refreshNonce();
    }
};
/**
 * Fetch current browser cart via WC Store API.
 * Returns a slim array of cart items for the backend to use instead of
 * server-side WC()->cart (which may have a different session).
 */
const fetchBrowserCart = async () => {
    try {
        // Don't send storeApiNonce - it may be stale from Varnish/Breeze cache.
        // GET /cart works with session cookies alone and returns fresh nonce in headers.
        const resp = await fetch('/wp-json/wc/store/v1/cart', {
            credentials: 'same-origin',
        });
        if (!resp.ok)
            return null;
        const data = await resp.json();
        if (!Array.isArray(data.items))
            return null;
        return data.items.map((i) => ({
            key: i.key,
            id: i.id,
            name: i.name,
            quantity: i.quantity,
            product_id: i.extensions?.woocommerce_product_id || undefined,
            variation_id: i.id !== i.extensions?.woocommerce_product_id ? i.id : undefined,
            prices: i.prices ? { price: i.prices.price, currency_code: i.prices.currency_code } : undefined,
        }));
    }
    catch {
        return null;
    }
};
/**
 * Generic fetch wrapper for API calls
 * Proactively refreshes nonce on first call, retries on 403
 */
const fetchAPI = async (endpoint, options = {}, isRetry = false) => {
    // Proactively refresh nonce on first API call to avoid stale page-cached nonces
    if (!isRetry) {
        await ensureFreshNonce();
    }
    const { restUrl, nonce } = getApiConfig();
    const url = `${restUrl}${endpoint}`;
    try {
        const response = await fetch(url, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...(nonce ? { 'X-WP-Nonce': nonce } : {}),
                ...options.headers,
            },
        });
        // If 403 and not a retry, try refreshing nonce and retrying once
        if (response.status === 403 && !isRetry) {
            cachedNonce = null;
            nonceRefreshed = false;
            const freshNonce = await refreshNonce();
            if (freshNonce) {
                return fetchAPI(endpoint, options, true);
            }
        }
        if (!response.ok) {
            const error = await response.json().catch(() => ({
                message: `HTTP error! status: ${response.status}`,
                code: response.status.toString(),
            }));
            throw new Error(error.message || 'Request failed');
        }
        return response.json();
    }
    catch (error) {
        if (error instanceof Error) {
            throw error;
        }
        throw new Error('An unexpected error occurred');
    }
};
/**
 * Validate chat request
 */
const validateChatRequest = (request) => {
    if (!request.message || typeof request.message !== 'string') {
        throw new Error('Message is required and must be a string');
    }
    if (request.message.trim().length === 0) {
        throw new Error('Message cannot be empty');
    }
    if (request.message.length > 5000) {
        throw new Error('Message is too long (max 5000 characters)');
    }
    if (request.sessionId && typeof request.sessionId !== 'string') {
        throw new Error('Session ID must be a string');
    }
};
/**
 * LocalStorage key for session ID
 */
const SESSION_STORAGE_KEY = 'woo_ai_session_id';
/**
 * Save session ID to localStorage
 */
const saveSessionId = (sessionId) => {
    try {
        localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    }
    catch (error) {
        console.warn('Failed to save session ID to localStorage:', error);
    }
};
/**
 * Get session ID from localStorage
 */
const getSessionId = () => {
    try {
        return localStorage.getItem(SESSION_STORAGE_KEY);
    }
    catch (error) {
        console.warn('Failed to get session ID from localStorage:', error);
        return null;
    }
};
/**
 * Clear session ID from localStorage
 */
const clearSessionId = () => {
    try {
        localStorage.removeItem(SESSION_STORAGE_KEY);
    }
    catch (error) {
        console.warn('Failed to clear session ID from localStorage:', error);
    }
};
/**
 * Chat Service Class
 *
 * Handles all chat-related API operations
 */
class ChatService {
    constructor() {
        Object.defineProperty(this, "baseEndpoint", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: '/woo-ai/v1/chat'
        });
    }
    /**
     * Send a chat message to the AI
     *
     * @param message - User message
     * @param sessionId - Optional session ID for conversation continuity
     * @param context - Optional context information
     * @returns Promise with chat response
     */
    async sendMessage(message, sessionId, context) {
        const request = {
            message,
            sessionId,
            context,
        };
        // Validate request
        validateChatRequest(request);
        try {
            // Fetch browser cart so backend get_cart uses real cart state (avoids session mismatch).
            const browserCart = await fetchBrowserCart();
            const response = await fetchAPI(`${this.baseEndpoint}/message`, {
                method: 'POST',
                body: JSON.stringify({
                    message: request.message,
                    session_id: request.sessionId,
                    context: request.context,
                    current_product_id: window.wooAIChatConfig?.currentProductId || undefined,
                    page_context: window.wooAIChatConfig?.pageContext || undefined,
                    browser_language: (navigator.language || 'en').split('-')[0].toLowerCase(),
                    ...(browserCart ? { browser_cart: browserCart } : {}),
                    ...(context?.image ? { image: context.image } : {}),
                }),
            });
            return {
                sessionId: response.session_id,
                message: response.message?.content || '',
                products: response.message?.metadata?.products || response.products || [],
                suggestions: response.message?.metadata?.suggestions || response.suggestions || [],
                tool_results: response.message?.metadata?.tool_results || response.tool_results || [],
                metadata: response.context ? { context: response.context } : undefined,
                context: response.context,
                cart_actions: response.cart_actions || [],
                cart: response.cart,
            };
        }
        catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to send message: ${error.message}`);
            }
            throw new Error('Failed to send message: Unknown error');
        }
    }
    /**
     * Send a chat message to the AI using SSE streaming
     *
     * @param message - User message
     * @param sessionId - Optional session ID for conversation continuity
     * @param context - Optional context information
     * @param callbacks - Optional callbacks for streaming events
     * @returns Promise with chat response (accumulated from stream)
     */
    async sendMessageStream(message, sessionId, context, callbacks) {
        const request = {
            message,
            sessionId,
            context,
        };
        validateChatRequest(request);
        await ensureFreshNonce();
        const { restUrl, nonce: freshNonce } = getApiConfig();
        // Fetch browser cart so backend get_cart uses real cart state (avoids session mismatch).
        const browserCart = await fetchBrowserCart();
        let response;
        try {
            response = await fetch(`${restUrl}/woo-ai/v1/chat/stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(freshNonce ? { 'X-WP-Nonce': freshNonce } : {}),
                },
                body: JSON.stringify({
                    message: request.message,
                    session_id: request.sessionId,
                    context: request.context,
                    current_product_id: window.wooAIChatConfig?.currentProductId || undefined,
                    page_context: window.wooAIChatConfig?.pageContext || undefined,
                    browser_language: (navigator.language || 'en').split('-')[0].toLowerCase(),
                    ...(browserCart ? { browser_cart: browserCart } : {}),
                    ...(context?.image ? { image: context.image } : {}),
                }),
            });
        }
        catch (fetchError) {
            console.warn('[WooAI] Stream fetch failed, falling back to regular API:', fetchError);
            return this.sendMessage(message, sessionId, context);
        }
        if (!response.ok || !response.body) {
            console.warn('[WooAI] Stream response not ok, falling back to regular API:', response.status);
            return this.sendMessage(message, sessionId, context);
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let fullText = '';
        let meta = {};
        let doneData = {};
        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done)
                    break;
                buffer += decoder.decode(value, { stream: true });
                // Process complete events (separated by \n\n)
                const events = buffer.split('\n\n');
                buffer = events.pop() || ''; // Keep incomplete event in buffer
                for (const event of events) {
                    if (!event.trim())
                        continue;
                    const lines = event.split('\n');
                    let eventType = '';
                    let eventData = '';
                    for (const line of lines) {
                        if (line.startsWith('event: ')) {
                            eventType = line.slice(7).trim();
                        }
                        else if (line.startsWith('data: ')) {
                            eventData = line.slice(6);
                        }
                    }
                    if (!eventType || !eventData)
                        continue;
                    // Flag set inside the inner try so we can rethrow outside it if needed.
                    let sseErrorToThrow = null;
                    try {
                        const parsed = JSON.parse(eventData);
                        switch (eventType) {
                            case 'token':
                                fullText += parsed.text || '';
                                callbacks?.onToken?.(parsed.text || '');
                                break;
                            case 'meta':
                                meta = parsed;
                                callbacks?.onMeta?.(parsed);
                                break;
                            case 'done':
                                doneData = parsed;
                                callbacks?.onDone?.(parsed);
                                break;
                            case 'error':
                                if (callbacks?.onError) {
                                    callbacks.onError(parsed.message || 'Stream error');
                                }
                                // Only throw if no tokens received yet - allows fallback to non-streaming.
                                // After tokens received, don't throw (avoids duplicate requests/cart actions).
                                if (!fullText) {
                                    sseErrorToThrow = new Error(parsed.message || 'Stream error');
                                }
                                break;
                        }
                    }
                    catch (e) {
                        console.warn('[WooAI] Failed to parse SSE event:', eventData);
                    }
                    // Rethrow SSE error outside the inner catch so the outer catch can handle fallback.
                    if (sseErrorToThrow) {
                        throw sseErrorToThrow;
                    }
                }
            }
        }
        catch (streamError) {
            console.warn('[WooAI] Stream read error, falling back to regular API:', streamError);
            return this.sendMessage(message, sessionId, context);
        }
        return {
            sessionId: doneData.session_id || sessionId || '',
            message: fullText,
            products: meta.products || [],
            suggestions: meta.suggestions || [],
            tool_results: [],
            cart_actions: meta.cart_actions || [],
            cart: meta.cart,
        };
    }
    /**
     * Get chat history for a session
     *
     * @param sessionId - Session ID to retrieve history for
     * @returns Promise with array of chat messages
     */
    async getHistory(sessionId, options = {}) {
        if (!sessionId || typeof sessionId !== 'string') {
            throw new Error('Valid session ID is required');
        }
        const { limit, offset } = options;
        try {
            // Build query string with optional pagination params
            const params = new URLSearchParams({ session_id: sessionId });
            if (limit !== undefined) {
                params.append('limit', limit.toString());
            }
            if (offset !== undefined) {
                params.append('offset', offset.toString());
            }
            const response = await fetchAPI(`${this.baseEndpoint}/session?${params.toString()}`, {
                method: 'GET',
            });
            const history = response.session?.conversation_history || [];
            return history.map((item, index) => {
                const message = {
                    id: `history-${index}-${item.role}-${Date.now()}`,
                    role: item.role === 'assistant' ? 'assistant' : 'user',
                    content: item.content || '',
                    timestamp: item.timestamp ? new Date(item.timestamp).getTime() : Date.now(),
                    metadata: item.metadata || undefined,
                };
                // Add products and suggestions if present in metadata
                if (item.metadata) {
                    if (item.metadata.products && Array.isArray(item.metadata.products)) {
                        message.products = item.metadata.products;
                    }
                    if (item.metadata.suggestions && Array.isArray(item.metadata.suggestions)) {
                        message.suggestions = item.metadata.suggestions;
                    }
                    // Add tool_results if present (for backward compatibility with conversation history)
                    if (item.metadata.tool_results && Array.isArray(item.metadata.tool_results)) {
                        message.tool_results = item.metadata.tool_results;
                    }
                }
                // Also check root level tool_results (from conversation_history)
                if (item.tool_results && Array.isArray(item.tool_results)) {
                    message.tool_results = item.tool_results;
                }
                // If products were not stored in metadata, derive them from tool_results (backward compatibility).
                if ((!message.products || message.products.length === 0) && message.tool_results) {
                    const searchProductsResult = message.tool_results.find((tr) => tr.tool === 'search_products' && Array.isArray(tr.result?.products));
                    if (searchProductsResult?.result?.products) {
                        message.products = searchProductsResult.result.products;
                    }
                }
                return message;
            });
        }
        catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to get history: ${error.message}`);
            }
            throw new Error('Failed to get history: Unknown error');
        }
    }
    /**
     * Clear a chat session
     *
     * @param sessionId - Session ID to clear
     * @returns Promise that resolves when session is cleared
     */
    async clearSession(sessionId) {
        if (!sessionId || typeof sessionId !== 'string') {
            throw new Error('Valid session ID is required');
        }
        try {
            await fetchAPI(`${this.baseEndpoint}/session/${encodeURIComponent(sessionId)}`, {
                method: 'DELETE',
            });
            // Clear from localStorage
            clearSessionId();
        }
        catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to clear session: ${error.message}`);
            }
            throw new Error('Failed to clear session: Unknown error');
        }
    }
    /**
     * Get active session or create a new one
     *
     * @param context - Optional context for session creation
     * @returns Promise with session ID
     */
    async getOrCreateSession(context) {
        try {
            // Check localStorage first for existing session
            const storedSessionId = getSessionId();
            if (storedSessionId) {
                // Verify session is still valid
                try {
                    const session = await this.getSessionById(storedSessionId);
                    if (session) {
                        return storedSessionId;
                    }
                }
                catch (error) {
                    // Session expired or invalid, clear it and create new one
                    console.warn('Stored session is invalid, creating new one:', error);
                    clearSessionId();
                }
            }
            // Create new session
            const payload = {};
            if (context?.userId) {
                payload.user_id = context.userId;
            }
            const response = await fetchAPI(`${this.baseEndpoint}/session`, {
                method: 'POST',
                body: Object.keys(payload).length ? JSON.stringify(payload) : undefined,
            });
            // Save to localStorage
            saveSessionId(response.session_id);
            return response.session_id;
        }
        catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to create session: ${error.message}`);
            }
            throw new Error('Failed to create session: Unknown error');
        }
    }
    /**
     * Get session by ID (for validation)
     *
     * @param sessionId - Session ID to retrieve
     * @returns Promise with session data or null if not found
     */
    async getSessionById(sessionId) {
        try {
            const response = await fetchAPI(`${this.baseEndpoint}/session?session_id=${encodeURIComponent(sessionId)}`, {
                method: 'GET',
            });
            return response.session || null;
        }
        catch {
            return null;
        }
    }
    /**
     * Send feedback for a message
     *
     * @param sessionId - Session ID
     * @param messageId - Message ID to provide feedback for
     * @param rating - Rating (1-5 or thumbs up/down)
     * @param comment - Optional feedback comment
     * @returns Promise that resolves when feedback is submitted
     */
    /**
     * Track an event in the chat session
     *
     * @param sessionId - Session ID
     * @param eventType - Type of event
     * @param eventData - Event data
     * @returns Promise that resolves when event is tracked
     */
    async trackEvent(sessionId, eventType, eventData) {
        if (!sessionId || !eventType) {
            throw new Error('Session ID and event type are required');
        }
        try {
            await fetchAPI(`${this.baseEndpoint}/events`, {
                method: 'POST',
                body: JSON.stringify({
                    sessionId,
                    eventType,
                    eventData,
                    timestamp: Date.now(),
                }),
            });
        }
        catch (error) {
            // Event tracking is non-critical, just log the error
            console.warn('Failed to track event:', error);
        }
    }
}
// Export singleton instance
export default new ChatService();
// Export localStorage helpers
export { saveSessionId, getSessionId, clearSessionId };
